<template>
  <div class="program-graduate-upload">
    <div class="row">
      <div class="col-xl-6">
        <div class="form-group">
          <label class="control-label">Agency</label>
          <multiselect
            v-model='agency'
            :options="agencies"
            track-by="id"
            label="name"
            placeholder="All Agencies"
            :disabled=disableFilters
          ></multiselect>
        </div>
      </div>
      <div class="col-xl-6">
        <div class="form-group">
          <label class="control-label">Graduate</label>
          <multiselect
            v-model='graduate'
            :options="graduates"
            track-by="id"
            label="name"
            placeholder="Select a Graduate"
            :disabled=disableFilters
          ></multiselect>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="form-group">
          <div class="form-check abc-checkbox abc-checkbox-success">
            <input v-model='bulkImportViaCandidateId' type="checkbox" id="bulkImportViaCandidateId" class="form-check-input" />
            <label for="bulkImportViaCandidateId" class="form-check-label"><strong>Bulk Import via Candidate ID</strong></label>
          </div>
        </div><!-- /.form-group -->
      </div>
    </div>
    <hr />
    <div class="file-dropzone" ref="dropzone" v-show="dz !== null">
      <div class="upload dz-clickable" ref="upload">
        <font-awesome :icon="['far', 'cloud-upload']" /> Drop Files here or <span class="browse">browse</span>
      </div>
      <div class="files" v-if="dz !== null && files.length">
        <div class="file" v-for="file in files" :key="file.id">
          <div class="row">
            <div class="col-11 details">
              <div class="row">
                <div class="file-name col-12">
                  <span>{{ file.name}} ({{ file.size | formatSize }})</span>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <div class="progress">
                    <div class="progress-bar"
                      :class="progressColor(file)"
                      :style="'width: ' + file.progress +'%;'"
                      role="progressbar"
                      :aria-valuenow="file.progress"
                      aria-valuemin="0"
                      aria-valuemax="100">
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-1 actions d-flex align-items-center justify-content-center">
              <a href="#" @click.prevent="deleteFile(file)" class="btn btn-sm btn-outline-danger btn-cancel remove-file">
                <font-awesome :icon="['far', 'times']"/>
              </a>
            </div>
        </div>
        </div>
      </div>
      <div id="dropzone-files" class="d-none">
        <div></div>
      </div>
      <div id="dz-template" class="d-none" ref="template">
        <div></div>
      </div>
    </div>
  </div>
</template>

<script>
import { programComputed, graduateMethods } from 'vx/helpers'
import Dropzone from 'dropzone'
import Multiselect from 'vue-multiselect'
import { auth, http, apiUrl, alert } from 'utils'

export default {
  components: {
    Multiselect
  },

  data () {
    return {
      agency: null,
      agencies: [],
      graduate: null,
      graduates: [],
      dz: null,
      files: [],
      bulkImportViaCandidateId: false,
      disableFilters: false,
      url: 'graduates/documents'
    }
  },

  computed: {
    ...programComputed
  },

  created () {
    http.get(apiUrl(`programs/${this.program.id}/agencies-list`)).then(response => {
      if (response.data && response.data.data) {
        this.agencies = response.data.data
        return
      }
      this.$noty.error('failed to get Agencies List')
    }).catch(() => {
      this.$noty.error('failed to get Agencies List')
    })
    this.getGraduatesList()
  },

  watch: {
    bulkImportViaCandidateId (value) {
      if (value) {
        this.agency = null
        this.graduate = null
        this.disableFilters = true

        if (this.dz !== null) {
          this.clearDz()
        }
        if (this.dz === null) {
          this.url = apiUrl(this.url)
          this.mountDropZone()
        }
      } else {
        this.disableFilters = false
        if (this.dz !== null) {
          this.clearDz()
        }
      }
    },
    agency () {
      this.getGraduatesList()
    },
    graduate (newValue, oldValue) {
      if (newValue) {
        if (this.dz !== null && (oldValue !== newValue)) {
          this.clearDz()
        }
        if (this.dz !== null && this.dz.files) {
          this.dz.removeAllFiles(true)
          this.updateFiles()
          return
        }
        if (this.dz === null) {
          this.url = apiUrl(this.url) + `&graduate_id=${this.graduate.id}`
          this.mountDropZone()
        }
        return
      }
      if (this.dz !== null) {
        this.clearDz()
      }
    }
  },
  methods: {
    ...graduateMethods,
    getGraduatesList () {
      let params = {}

      if (this.agency !== null) {
        params.agency = this.agency.id
      }

      http.get(apiUrl(`programs/${this.program.id}/graduates-list`), { params }).then(response => {
        if (response.data && response.data.data) {
          this.graduates = response.data.data
          return
        }

        this.$noty.error('failed to get Graduates List')
      }).catch(() => {
        this.$noty.error('failed to get Graduates List')
      })
    },

    handleUploadErrorCallback (file, message) {
      this.$noty.error('There was a problem uploading a file.')
      this.updateFiles()
    },

    handleUploadSuccessCallback (file, response) {
      if (response.data) {
        if (file.id === undefined || file.id === null) {
          file.id = response.data.id
        }
        this.updateFiles()
      }
      if (response.status === 'error') {
        this.$noty.error(response.message)
      }
    },

    mountDropZone () {
      this.dz = new Dropzone(this.$refs.dropzone, {
        url: this.url,
        withCredentials: true,
        maxFilesize: 5,
        acceptedFiles: '.pdf, .docx, .xlsx, .pptx',
        clickable: this.$refs.upload,
        previewTemplate: this.$refs.template.innerHTML,
        previewsContainer: `#dropzone-files`,
        createImageThumbnails: false,
        disablePreviews: true,
        headers: {
          Authorization: `Bearer ${auth.getToken()}`
        }
      })

      this.dz.on('sending', this.updateFiles)
      this.dz.on('success', this.handleUploadSuccessCallback)
      this.dz.on('error', this.handleUploadErrorCallback)
    },

    progressColor (file) {
      let color
      switch (file.status) {
        case 'success':
          color = 'bg-success'
          break
        case 'error':
          color = 'bg-danger'
          break
        default:
          color = 'bg-info'
      }
      return color
    },

    updateFiles () {
      if (this.dz !== null && this.dz.files !== undefined) {
        this.files = this.dz.files.map(file => {
          return {
            id: file.id || null,
            status: file.status,
            progress: file.upload.progress,
            name: file.name,
            size: file.size
          }
        })
      }
    },

    async deleteFile (file) {
      if (file.id === undefined || file.id === null) {
        this.removeFile(file)
      }

      const confirm = await alert.confirm({
        title: 'Delete File?',
        text: 'Are you sure you want to delete this file?'
      })

      if (confirm) {
        await this.deleteDocument({ graduate: this.graduate.id, document: file.id })
        this.removeFile(file)
      }
    },

    removeFile (file) {
      const dzFile = this.dz.files.find(option => {
        return (option.id === file.id || (option.name === file.name && option.size === file.size))
      })
      this.dz.removeFile(dzFile)
      this.updateFiles()
    },

    clearDz () {
      this.dz.destroy()
      this.dz = null
    }
  },

  filters: {
    formatSize (value) {
      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      const step = 1024
      value = parseFloat(value, 10)
      let i = 0
      for (i; i < units.length; i++) {
        if (value < Math.pow(step, i)) {
          if (units[i - 1]) {
            return (value / Math.pow(step, i - 1)).toFixed(2) + units[i - 1]
          }
          return value + units[i]
        }
      }
      return (value / Math.pow(step, i - 1)).toFixed(2) + units[i - 1]
    }
  }
}
</script>

<style scoped>
  .upload {
    border: 2px dashed #2E5299;
    color: #2E5299;
    background: rgba(32, 161, 182, 0.06);
    height: 8rem;
    display: block;
    text-align: center;
    line-height: 7.6875rem;
    font-size: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    cursor: pointer;
  }

  .upload .browse {
    color: #20396a;
  }

  .delete-button {
    cursor: pointer;
  }
</style>
